import React, { useState } from 'react';
import { Modal } from './Modal';
import { MediaItem, MediaItemData } from '@/core/MediaItem/MediaItem.model';
import { SpinnerIcon, DownloadIcon } from '@/components/icons';
import { useRole } from '@/contexts/RoleContext';

type DownloadStatus = 'idle' | 'checking' | 'downloading' | 'ready' | 'failed' | 'not_available';

interface DownloadQualityModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaData: MediaItemData;
  onDownloadStart?: (quality: '4k' | 'compressed') => void;
  onDownloadProgress?: (status: DownloadStatus) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function estimateDownloadTime(bytes: number): string {
  const megabytesPerSecond = 10;
  const seconds = bytes / (megabytesPerSecond * 1024 * 1024);
  if (seconds < 60) return `~${Math.ceil(seconds)}s`;
  const minutes = Math.ceil(seconds / 60);
  return `~${minutes} min`;
}

export const DownloadQualityModal: React.FC<DownloadQualityModalProps> = ({
  isOpen,
  onClose,
  mediaData,
  onDownloadStart,
  onDownloadProgress,
}) => {
  const { role, clientId, token } = useRole();
  const [status4k, setStatus4k] = useState<DownloadStatus>('idle');
  const [statusCompressed, setStatusCompressed] = useState<DownloadStatus>('idle');

  const isVideo = mediaData.mediaType === 'video';
  const fileSize = mediaData.fileSize;

  const handleDownload4k = async () => {
    if (status4k !== 'idle') return;
    onDownloadStart?.('4k');

    const mediaItem = new MediaItem(mediaData, role, clientId || mediaData.clientId, token);
    try {
      await mediaItem.downloadFile(role, (s) => {
        setStatus4k(s);
        onDownloadProgress?.(s);
        if (s === 'ready' || s === 'failed') {
          setTimeout(() => {
            setStatus4k('idle');
            if (s === 'ready') onClose();
          }, 1500);
        }
      });
    } catch {
      setStatus4k('failed');
      setTimeout(() => setStatus4k('idle'), 3000);
    }
  };

  const handleDownloadCompressed = async () => {
    if (statusCompressed !== 'idle') return;
    onDownloadStart?.('compressed');

    const mediaItem = new MediaItem(mediaData, role, clientId || mediaData.clientId, token);
    try {
      await mediaItem.downloadCompressedFile(role, (s) => {
        setStatusCompressed(s);
        onDownloadProgress?.(s);
        if (s === 'ready') {
          setTimeout(() => {
            setStatusCompressed('idle');
            onClose();
          }, 1500);
        } else if (s === 'failed') {
          setTimeout(() => setStatusCompressed('idle'), 3000);
        }
      });
    } catch {
      setStatusCompressed('failed');
      setTimeout(() => setStatusCompressed('idle'), 3000);
    }
  };

  const isBusy = status4k !== 'idle' || (statusCompressed !== 'idle' && statusCompressed !== 'not_available');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Scarica" size="sm">
      <div className="p-4 space-y-3">
        {/* 4K / Original option */}
        <button
          onClick={handleDownload4k}
          disabled={isBusy}
          className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">4K - Originale</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fileSize
                  ? `${formatFileSize(fileSize)}${isVideo ? ` - ${estimateDownloadTime(fileSize)}` : ''}`
                  : 'Dimensione sconosciuta'}
              </p>
            </div>
            <div className="shrink-0 ml-3">
              {status4k === 'idle' && <DownloadIcon className="w-5 h-5 text-gray-400" />}
              {(status4k === 'checking' || status4k === 'downloading') && (
                <SpinnerIcon className="w-5 h-5 text-blue-500" />
              )}
              {status4k === 'ready' && (
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {status4k === 'failed' && (
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          </div>
        </button>

        {/* Compressed option */}
        <button
          onClick={handleDownloadCompressed}
          disabled={isBusy || statusCompressed === 'not_available'}
          className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Risoluzione Media</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isVideo ? 'Proxy 720p MP4' : 'WebP ottimizzato (max 1400x1400)'}
              </p>
              {statusCompressed === 'not_available' && (
                <p className="text-xs text-amber-600 mt-1">Non disponibile - usa 4K</p>
              )}
            </div>
            <div className="shrink-0 ml-3">
              {statusCompressed === 'idle' && <DownloadIcon className="w-5 h-5 text-gray-400" />}
              {statusCompressed === 'checking' && (
                <SpinnerIcon className="w-5 h-5 text-green-500" />
              )}
              {statusCompressed === 'ready' && (
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {statusCompressed === 'failed' && (
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {statusCompressed === 'not_available' && (
                <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
            </div>
          </div>
        </button>
      </div>
    </Modal>
  );
};
